// @px7digital/system-libraries/accounts-client
//
// Shared client surface for PX7 Accounts at accounts.px7.digital
// (accounts-cto.pages.dev is the underlying Pages alias). Two halves:
//
//   Browser — drive sign-in, consume the redirect fragment, hold the JWT
//             in localStorage, refresh transparently, and decorate fetch
//             calls with `Authorization: Bearer`.
//
//   Server  — verify a JWT against the accounts JWKS, with an in-memory
//             1-hour cache. Designed for Cloudflare Pages Functions /
//             Workers but runs anywhere with Web Crypto.
//
// Single entry point so the app side imports cleanly:
//
//   import { signInUrl, consumeReturn, getStoredToken, refreshToken,
//            signOut, authFetch, verifyToken,
//            subscribe, getAuthStatus, revalidate, AuthExpiredError }
//     from "@px7digital/system-libraries/accounts-client";
//
// `verifyToken` lives here too rather than in a separate `/server`
// export to keep the shared-lib `package.json` minimal — bundlers tree-
// shake the unused half.
//
// Auth-status events (added 2026-05-16). Consumer apps that store the
// JWT in localStorage (currently only UrbanCrop) can subscribe to a
// module-scoped event stream that fires on every transition between
// the three states `"signed-in" | "signed-out" | "expired"`. The
// emitter is internal to this module — apps never import the Set,
// they call `subscribe(cb)` and receive an unsubscribe handle.
//
// `expired` is the new state. It fires when:
//   • authFetch's initial AND refreshed call both come back 401, or
//   • revalidate({ app, accountsOrigin }) hits a server-side 401.
// Together those cover "JWT exp drifted past server's clock" and
// "server keypair rotated / account deleted / refresh token revoked
// out of band" — the two ways a token can be dead beyond what the
// access-token `exp` clock can see locally.

import { jwtVerify, createRemoteJWKSet } from "jose";

const STORAGE_PREFIX = "px7_accounts:";

function storageKey(appId) {
  return `${STORAGE_PREFIX}${appId}`;
}

function defaultAccountsOrigin() {
  // The custom domain is the canonical surface. Apps can still override
  // with `VITE_ACCOUNTS_ORIGIN` at build time (useful for staging /
  // preview builds that want to point at a Pages alias instead).
  try {
    const value =
      typeof import.meta !== "undefined" &&
      import.meta.env &&
      typeof import.meta.env.VITE_ACCOUNTS_ORIGIN === "string"
        ? import.meta.env.VITE_ACCOUNTS_ORIGIN.trim()
        : "";
    if (value) return value.replace(/\/+$/, "");
  } catch {
    // import.meta unavailable (server-side); fall through.
  }
  return "https://accounts.px7.digital";
}

// ── Browser: auth-status event emitter ───────────────────────────────
//
// Tiny pub/sub. Lives module-scoped so every import in the same bundle
// shares the same listener Set — that's what makes the topbar pill and
// the App-level reauth banner stay in lockstep without prop-drilling.
// No external lib; a Set + a forEach is plenty.

const _listeners = new Set();

function emit(event) {
  // Snapshot before iterating so a listener that unsubscribes itself
  // mid-fire doesn't trip the iterator.
  const snapshot = [...listenersSnapshot()];
  for (const cb of snapshot) {
    try { cb(event); } catch { /* swallow — one bad listener can't break others */ }
  }
}

function listenersSnapshot() {
  return _listeners;
}

/**
 * Subscribe to auth-status changes. The callback receives
 * `{ app, status, user? }` on every state transition. `status` is one
 * of `"signed-in" | "signed-out" | "expired"`. `user` is present on
 * `signed-in` only and carries `{ email, userId }`.
 *
 * Returns an unsubscribe function. Always call it on unmount —
 * leaking listeners across HMR reloads is the usual gotcha.
 *
 * @param {(event: { app: string, status: string, user?: { email: string, userId: string } }) => void} callback
 * @returns {() => void}
 */
export function subscribe(callback) {
  if (typeof callback !== "function") return () => {};
  _listeners.add(callback);
  return () => { _listeners.delete(callback); };
}

/**
 * Synchronous read of current auth status — no network. Returns one of
 * `"signed-in" | "signed-out" | "expired"`. Uses the same `exp` skew
 * convention as `getStoredToken`. Cheap to call on every render.
 *
 *   no payload          → "signed-out"
 *   payload, exp passed → "expired"
 *   payload, exp future → "signed-in"
 *
 * @param {{ app: string }} options
 * @returns {"signed-in" | "signed-out" | "expired"}
 */
export function getAuthStatus({ app } = {}) {
  if (typeof window === "undefined") return "signed-out";
  if (!app) throw new Error("getAuthStatus: missing app id");
  const stored = getStoredTokenEvenExpired({ app });
  if (!stored || !stored.token) return "signed-out";
  const now = Math.floor(Date.now() / 1000);
  if (stored.exp && stored.exp < now - 60) return "expired";
  return "signed-in";
}

// Tagged error so callers can branch on "auth dead" vs "network /
// server dead" without poking at HTTP status codes. authFetch throws
// one of these when both the initial call and the refreshed retry
// come back 401.
export class AuthExpiredError extends Error {
  constructor(message = "PX7 Accounts session expired") {
    super(message);
    this.name = "AuthExpiredError";
    this.code = "auth_expired";
  }
}

/**
 * Server-side validity probe. Uses `/api/auth/refresh` as the
 * primitive — a 200 there proves the refresh token is still alive on
 * the accounts server (keypair intact, grant active, user not
 * deleted), which is the strongest end-to-end signal short of a
 * dedicated Bearer-mode `/me` endpoint.
 *
 * Behavior:
 *   • No stored payload         → no-op (signed-out is already true).
 *   • Refresh succeeds (200)    → updates the stored access token,
 *                                 emits `signed-in`.
 *   • Refresh rejected (401)    → clears the stored payload, emits
 *                                 `expired`.
 *   • Network / 5xx error       → no-op. Don't punish the user for a
 *                                 transient outage with a false
 *                                 "expired" banner.
 *
 * Safe to call on app boot and on visibilitychange / focus. Doesn't
 * loop on its own — callers decide when to re-fire.
 *
 * @param {{ app: string, accountsOrigin?: string }} options
 * @returns {Promise<"signed-in" | "signed-out" | "expired">}
 */
export async function revalidate({ app, accountsOrigin } = {}) {
  if (typeof window === "undefined") return "signed-out";
  if (!app) throw new Error("revalidate: missing app id");

  const stored = getStoredTokenEvenExpired({ app });
  if (!stored || !stored.refresh) {
    // Nothing to validate; don't emit (the consumer already knows
    // it's signed-out from getAuthStatus on boot).
    return "signed-out";
  }

  const origin = (accountsOrigin || defaultAccountsOrigin()).replace(/\/+$/, "");
  let response;
  try {
    response = await fetch(`${origin}/api/auth/refresh`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ app_id: app, refresh: stored.refresh }),
    });
  } catch {
    // Network blip — leave state as-is. The caller can retry on the
    // next user action; punishing them with an "expired" banner over
    // a flaky wifi moment would be worse than the bug we're fixing.
    return getAuthStatus({ app });
  }

  if (response.status === 401) {
    try { localStorage.removeItem(storageKey(app)); } catch { /* noop */ }
    emit({ app, status: "expired" });
    return "expired";
  }

  if (!response.ok) {
    // 5xx / 4xx-other — leave state alone, same reasoning as a
    // network blip.
    return getAuthStatus({ app });
  }

  let data;
  try {
    data = await response.json();
  } catch {
    return getAuthStatus({ app });
  }
  if (!data?.ok || !data.token) return getAuthStatus({ app });

  const next = {
    ...stored,
    token: data.token,
    exp: Number(data.exp || 0),
  };
  try {
    localStorage.setItem(storageKey(app), JSON.stringify(next));
  } catch {
    // ignore
  }
  emit({
    app,
    status: "signed-in",
    user: { email: next.email || "", userId: next.userId || "" },
  });
  return "signed-in";
}

// ── Browser: sign-in URL builder ──────────────────────────────────────

/**
 * Build the `/authorize` URL for the SSO flow. The app passes its own
 * id (`urbancrop`, `stylesignal`, …) and the URL the accounts service
 * should redirect back to after a successful sign-in. The accounts
 * service redirects to `<return>#token=…&refresh=…&exp=…`.
 *
 * @param {{ app: string, return: string, accountsOrigin?: string }} options
 * @returns {string}
 */
export function signInUrl({ app, return: returnUrl, accountsOrigin } = {}) {
  if (!app) throw new Error("signInUrl: missing app id");
  if (!returnUrl) throw new Error("signInUrl: missing return URL");
  const origin = (accountsOrigin || defaultAccountsOrigin()).replace(/\/+$/, "");
  const params = new URLSearchParams({ app, return: returnUrl });
  return `${origin}/authorize?${params.toString()}`;
}

// ── Browser: in-app password grant (overlay sign-in) ─────────────────
//
// The redirect-free alternative to `signInUrl`. The app collects the
// email + password in its own styled overlay and posts them straight to
// the accounts service over CORS; the response carries the app-scoped
// JWT + refresh in its JSON body (see PX7 Accounts `/api/auth/login` with
// an `app_id`). On success the payload is stored under the same
// localStorage namespace `consumeReturn` uses, so the rest of the
// accounts-client surface (`getStoredToken`, `authFetch`, `refreshToken`,
// auth-status events) works identically whether the user signed in via
// the redirect flow or the overlay.
//
// Security note: this is a first-party password grant. Use it only in
// apps that PX7 wholly owns — the password is typed into the app's own
// origin. CORS on the accounts side restricts which origins may read the
// response to the app's allowlist.

function storeAuthPayload({ app, data }) {
  const payload = {
    token: data.token,
    refresh: data.refresh || "",
    exp: Number(data.exp || 0),
    email: (data.user && data.user.email) || data.email || "",
    userId: (data.user && data.user.id) || data.user_id || "",
  };
  try {
    localStorage.setItem(storageKey(app), JSON.stringify(payload));
  } catch {
    // Storage write failures (privacy mode, quota) are non-fatal; the
    // token still lives in memory for this load via the return value.
  }
  emit({
    app,
    status: "signed-in",
    user: { email: payload.email, userId: payload.userId },
  });
  return payload;
}

/**
 * Tagged error for a rejected credential exchange. `code` is the
 * machine-readable reason (`invalid_credentials`, `account_exists`,
 * `weak_password`, `unknown_app`, `origin_not_allowed`, `network`,
 * `request_failed`) and `message` is the human-facing string. UI can
 * branch on `code` while showing `message`.
 */
export class AuthRequestError extends Error {
  constructor(message, code) {
    super(message || "Sign-in failed");
    this.name = "AuthRequestError";
    this.code = code || "request_failed";
  }
}

function codeForStatus(status, data) {
  const raw = data && typeof data.error === "string" ? data.error : "";
  if (raw === "unknown_app" || raw === "origin_not_allowed") return raw;
  if (status === 401) return "invalid_credentials";
  if (status === 409) return "account_exists";
  if (status === 400 && /password/i.test(raw)) return "weak_password";
  return "request_failed";
}

async function postCredentials({ path, app, email, password, accountsOrigin, extra }) {
  if (!app) throw new Error(`${path}: missing app id`);
  const origin = (accountsOrigin || defaultAccountsOrigin()).replace(/\/+$/, "");
  let response;
  try {
    response = await fetch(`${origin}${path}`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password, app_id: app, ...(extra || {}) }),
    });
  } catch {
    throw new AuthRequestError(
      "Couldn't reach PX7 Accounts. Check your connection and try again.",
      "network",
    );
  }
  let data = null;
  try {
    data = await response.json();
  } catch {
    data = null;
  }
  if (!response.ok || !data || !data.ok || !data.token) {
    const message = (data && data.error) || "Sign-in failed. Try again.";
    throw new AuthRequestError(message, codeForStatus(response.status, data));
  }
  return storeAuthPayload({ app, data });
}

/**
 * Exchange email + password for an app-scoped session, no redirect.
 * Stores the token under the app namespace and emits `signed-in`.
 * Throws `AuthRequestError` (with a `.code`) on failure.
 *
 * @param {{ app: string, email: string, password: string, accountsOrigin?: string }} options
 * @returns {Promise<{ token: string, refresh: string, exp: number, email: string, userId: string }>}
 */
export async function loginWithPassword({ app, email, password, accountsOrigin } = {}) {
  return postCredentials({ path: "/api/auth/login", app, email, password, accountsOrigin });
}

/**
 * Create a PX7 Account and sign straight into the app, no redirect.
 * Same storage + event behavior as `loginWithPassword`. The new account
 * starts unverified; a verification email is sent server-side.
 *
 * @param {{ app: string, email: string, password: string, accountsOrigin?: string }} options
 * @returns {Promise<{ token: string, refresh: string, exp: number, email: string, userId: string }>}
 */
export async function signupWithPassword({ app, email, password, accountsOrigin } = {}) {
  return postCredentials({ path: "/api/auth/signup", app, email, password, accountsOrigin });
}

/**
 * Kick off a password reset for an email. Best-effort and intentionally
 * non-committal about whether the email exists (the server returns 200
 * either way). Resolves true when the request was accepted.
 *
 * @param {{ email: string, accountsOrigin?: string }} options
 * @returns {Promise<boolean>}
 */
export async function requestPasswordReset({ email, accountsOrigin } = {}) {
  const origin = (accountsOrigin || defaultAccountsOrigin()).replace(/\/+$/, "");
  try {
    const response = await fetch(`${origin}/api/auth/forgot`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });
    return response.ok;
  } catch {
    return false;
  }
}

// ── Browser: consume the redirect fragment ───────────────────────────

/**
 * Read the access token, refresh token, expiry, and user info from the
 * URL fragment that `/authorize` redirected to, write them to
 * localStorage under the app's namespace, and strip the fragment from
 * the URL so a back/forward never re-applies it. Returns the parsed
 * payload (or null when there's nothing to consume).
 *
 * Call this once at app boot (before any other auth check).
 *
 * @param {{ app: string }} options
 * @returns {{ token: string, refresh: string, exp: number, email: string, userId: string } | null}
 */
export function consumeReturn({ app } = {}) {
  if (typeof window === "undefined") return null;
  if (!app) throw new Error("consumeReturn: missing app id");
  const hash = window.location.hash || "";
  if (!hash || !hash.includes("token=")) return null;

  const params = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
  const token = params.get("token") || "";
  const refresh = params.get("refresh") || "";
  const exp = Number(params.get("exp") || 0);
  const email = params.get("email") || "";
  const userId = params.get("user_id") || "";
  if (!token) return null;

  const payload = { token, refresh, exp, email, userId };
  try {
    localStorage.setItem(storageKey(app), JSON.stringify(payload));
  } catch {
    // Storage write failures (privacy mode, quota) are non-fatal; the
    // token still lives in memory for this load.
  }

  // Strip the fragment without disturbing path/search.
  try {
    const url = new URL(window.location.href);
    url.hash = "";
    window.history.replaceState({}, "", `${url.pathname}${url.search}`);
  } catch {
    // ignore
  }

  // Tell the world a fresh sign-in just landed.
  emit({
    app,
    status: "signed-in",
    user: { email, userId },
  });

  return payload;
}

// ── Browser: token cache helpers ─────────────────────────────────────

/**
 * Read the stored auth payload for this app. Returns null when nothing
 * is stored, the stored JSON is malformed, or the token has already
 * expired (with a 60-second skew). Callers should call `refreshToken`
 * when this returns null but a refresh token is available.
 *
 * @param {{ app: string }} options
 * @returns {{ token: string, refresh: string, exp: number, email: string, userId: string } | null}
 */
export function getStoredToken({ app } = {}) {
  if (typeof window === "undefined") return null;
  if (!app) throw new Error("getStoredToken: missing app id");
  let raw = "";
  try {
    raw = localStorage.getItem(storageKey(app)) || "";
  } catch {
    return null;
  }
  if (!raw) return null;
  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed.token !== "string" || !parsed.token) return null;

  const now = Math.floor(Date.now() / 1000);
  if (parsed.exp && parsed.exp < now - 60) {
    // Already expired with skew — treat as absent so caller refreshes.
    return null;
  }
  return parsed;
}

/**
 * Read the stored payload including expired tokens (so callers can
 * still grab the refresh token to mint a new access token). Use this
 * in `refreshToken` only; user-facing code should use `getStoredToken`.
 */
function getStoredTokenEvenExpired({ app }) {
  if (typeof window === "undefined") return null;
  if (!app) return null;
  let raw = "";
  try {
    raw = localStorage.getItem(storageKey(app)) || "";
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/**
 * Exchange the stored refresh token for a fresh access token. Updates
 * localStorage on success. Returns the new payload (or null when the
 * refresh failed — caller should redirect to sign-in).
 *
 * @param {{ app: string, accountsOrigin?: string }} options
 * @returns {Promise<{ token: string, exp: number } | null>}
 */
export async function refreshToken({ app, accountsOrigin } = {}) {
  if (typeof window === "undefined") return null;
  if (!app) throw new Error("refreshToken: missing app id");
  const stored = getStoredTokenEvenExpired({ app });
  if (!stored || !stored.refresh) return null;
  const origin = (accountsOrigin || defaultAccountsOrigin()).replace(/\/+$/, "");

  let response;
  try {
    response = await fetch(`${origin}/api/auth/refresh`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ app_id: app, refresh: stored.refresh }),
    });
  } catch {
    return null;
  }
  if (!response.ok) {
    // Refresh rejected — drop the stored payload so getStoredToken
    // surfaces "signed out" cleanly.
    try { localStorage.removeItem(storageKey(app)); } catch { /* noop */ }
    return null;
  }
  let data;
  try {
    data = await response.json();
  } catch {
    return null;
  }
  if (!data?.ok || !data.token) return null;
  const next = {
    ...stored,
    token: data.token,
    exp: Number(data.exp || 0),
  };
  try {
    localStorage.setItem(storageKey(app), JSON.stringify(next));
  } catch {
    // ignore
  }
  return next;
}

// ── Browser: sign out (local) ─────────────────────────────────────────

/**
 * Clear this app's local auth payload. Doesn't touch the accounts
 * session cookie — "sign out everywhere" lives in /account on the
 * accounts service.
 */
export function signOut({ app } = {}) {
  if (typeof window === "undefined") return;
  if (!app) return;
  try { localStorage.removeItem(storageKey(app)); } catch { /* noop */ }
  emit({ app, status: "signed-out" });
}

// ── Browser: authFetch ────────────────────────────────────────────────

/**
 * `fetch` wrapper that attaches `Authorization: Bearer <token>` and
 * refreshes once on 401. The caller passes the app id so the wrapper
 * knows which slot to read/write.
 *
 * Failure modes:
 *   • No stored token and no refresh path → throws `AuthExpiredError`
 *     after emitting `expired`. (Old behavior was to synthesise a 401
 *     Response — callers had to inspect status codes. Tagged-error
 *     is unambiguous.)
 *   • Initial 401 → refresh once. If the refreshed call also 401s,
 *     emit `expired`, clear the stored payload, and throw
 *     `AuthExpiredError`. The App-level reauth surface takes over.
 *   • Any other status (incl. non-auth 4xx/5xx) → return the Response
 *     unchanged. The caller decides.
 *
 * @param {string} input The URL or Request first arg, same as fetch().
 * @param {RequestInit & { app: string, accountsOrigin?: string }} init
 * @returns {Promise<Response>}
 */
export async function authFetch(input, init = {}) {
  const { app, accountsOrigin, ...fetchInit } = init;
  if (!app) throw new Error("authFetch: missing app id");

  const callWith = async (token) => {
    const headers = new Headers(fetchInit.headers || {});
    if (token) headers.set("Authorization", `Bearer ${token}`);
    return fetch(input, { ...fetchInit, headers });
  };

  let stored = getStoredToken({ app });
  if (!stored) {
    // No live access token — try a refresh before giving up.
    stored = await refreshToken({ app, accountsOrigin });
  }
  if (!stored) {
    // No way to authenticate this call. Emit + throw so the App-level
    // banner can take over and the caller doesn't have to invent its
    // own 401-handling.
    emit({ app, status: "expired" });
    throw new AuthExpiredError();
  }

  let response = await callWith(stored.token);
  if (response.status !== 401) return response;

  // 401 — refresh once and retry.
  const refreshed = await refreshToken({ app, accountsOrigin });
  if (!refreshed) {
    emit({ app, status: "expired" });
    throw new AuthExpiredError();
  }
  const retried = await callWith(refreshed.token);
  if (retried.status === 401) {
    // Refresh worked at the protocol level but the server still
    // rejects the freshly-minted token — keypair rotated, audience
    // changed, etc. Treat as fully expired and clear local state so
    // the next render reflects the truth.
    try { localStorage.removeItem(storageKey(app)); } catch { /* noop */ }
    emit({ app, status: "expired" });
    throw new AuthExpiredError();
  }
  return retried;
}

// ── Server: JWT verification ─────────────────────────────────────────

const JWKS_CACHE = new Map();
const JWKS_TTL_MS = 60 * 60 * 1000; // 1 hour

function getJwks(jwksUrl) {
  const cached = JWKS_CACHE.get(jwksUrl);
  const now = Date.now();
  if (cached && cached.expiresAt > now) return cached.jwks;
  const jwks = createRemoteJWKSet(new URL(jwksUrl));
  JWKS_CACHE.set(jwksUrl, { jwks, expiresAt: now + JWKS_TTL_MS });
  return jwks;
}

/**
 * Verify a JWT issued by accounts.px7.digital.
 *
 * Throws if the signature is invalid, the audience doesn't match, or
 * the token has expired. Returns the parsed payload on success.
 *
 * @param {string} token The raw JWT.
 * @param {{ audience: string, jwksUrl?: string, issuer?: string }} options
 * @returns {Promise<{ sub: string, email: string, email_verified: boolean, aud: string, iss: string, iat: number, exp: number, jti: string }>}
 */
export async function verifyToken(token, options = {}) {
  if (!token || typeof token !== "string") {
    throw new Error("verifyToken: missing token");
  }
  if (!options.audience) {
    throw new Error("verifyToken: missing audience");
  }
  const jwksUrl = options.jwksUrl || `${defaultAccountsOrigin()}/.well-known/jwks.json`;
  const jwks = getJwks(jwksUrl);
  const verifyOptions = { audience: options.audience };
  if (options.issuer) verifyOptions.issuer = options.issuer;
  const { payload } = await jwtVerify(token, jwks, verifyOptions);
  return payload;
}

/**
 * Extract a Bearer token from a Request's Authorization header.
 * Returns "" when absent or malformed.
 *
 * @param {Request} request
 * @returns {string}
 */
export function readBearerToken(request) {
  const header = request.headers.get("Authorization") || "";
  const match = header.match(/^Bearer\s+(.+)$/i);
  return match?.[1]?.trim() || "";
}
